import { isObject } from '@direct-frontend/stdlib';

import { getErrorCauseChain } from './getErrorCauseChain';

/**
 * Ключ, под которым retry живет на объекте ошибки.
 *
 * Symbol, а не строковое поле: такой ключ переживает сериализацию SSR-дампа и
 * `JSON.stringify` — они его просто не видят, тогда как поле-функция на ошибке роняет
 * сериализацию. Наружу из пакета ключ не экспортируется, доступ только через хелперы.
 */
const ERROR_RETRY = Symbol('@adv-frontend/error/errorRetry');

/**
 * Функция, повторяющая операцию, которая завершилась ошибкой.
 */
export type ErrorRetry = () => void;

/**
 * Привязывает к объекту ошибки retry — функцию, повторяющую неудавшуюся операцию.
 *
 * @param error - Объект ошибки, которую можно повторить
 * @param retry - Функция, повторяющая неудавшуюся операцию
 *
 * @remarks
 * Запасной вход: основной — поле `retry` в опциях конструктора {@link BaseError}. Хелпер
 * нужен там, где в момент создания ошибки retry еще неизвестен или ошибку создает не
 * наш код (например, ошибка приходит plain-объектом).
 *
 * Retry пишется ровно на переданный объект и вверх по цепочке `cause` не поднимается;
 * читается он, наоборот, по всей цепочке — см. {@link getErrorRetry}.
 *
 * Вызов ничего не делает, если ошибка — не объект либо объект нерасширяемый (заморожен
 * или запечатан). В частности, размечать ошибку нужно до того, как ее объект заморозят.
 *
 * После SSR-гидрации retry у ошибки нет: сериализация Symbol-ключи не переносит. Это
 * допустимо — потребитель в таком случае работает так же, как с ошибкой без retry.
 *
 * @example
 * ```ts
 * setErrorRetry(error, () => {
 *     loadReports();
 * });
 * ```
 */
export function setErrorRetry(error: unknown, retry: ErrorRetry): void {
    if (!isObject(error) || !Object.isExtensible(error)) {
        return;
    }

    Object.defineProperty(error, ERROR_RETRY, {
        value: retry,
        writable: true,
        configurable: true,
        enumerable: false,
    });
}

/**
 * Возвращает retry, привязанный к ошибке или к любой ошибке в ее цепочке `cause`.
 *
 * @param err - Ошибка, пойманная Error Boundary, или любая другая
 * @returns Retry, либо `undefined`, если ни одно звено цепочки его не несет
 *
 * @remarks
 * Асимметрия с {@link setErrorRetry} намеренная: retry ставится на один конкретный объект,
 * а читается по всей цепочке — иначе каждый слой, оборачивающий чужую ошибку своей, обязан
 * был бы переносить retry руками. Обход спрятан в хелпер, чтобы не размазывать его по
 * потребителям.
 *
 * Цепочка идет от внешней ошибки к внутренней, поэтому собственный retry внешней ошибки
 * выигрывает у retry причины. Обход шагает и через настоящие `Error`, и через
 * plain-объекты, которыми `serializeError` заменяет причины (маркеры `$$BaseError$$` и
 * `$$NativeError$$`), и защищен от циклов в цепочке.
 *
 * Retry теряется, только если пересобрали то звено цепочки, которое его несет. Пересборка
 * бывает в одном случае: конструктор {@link BaseError} прогоняет свою причину через
 * `serializeError`, и не-BaseError причина становится новым объектом — нативная ошибка
 * plain-объектом с маркером, а plain-звено копией вместе со всем, что под ним.
 * BaseError-причина сохраняется по ссылке, а обертки, сами не наследующие `BaseError`,
 * не пересобирают ничего — сквозь них retry проходит.
 *
 * Практическое правило: привязывайте retry к наследнику `BaseError` и не прячьте его под
 * plain-звеном цепочки.
 *
 * @example
 * ```tsx
 * const retry = getErrorRetry(error);
 *
 * return <ErrorMessage onRetry={retry} />;
 * ```
 */
export function getErrorRetry(err: unknown): ErrorRetry | undefined {
    for (const link of getErrorCauseChain(err)) {
        const retry = readErrorRetry(link);

        if (retry) {
            return retry;
        }
    }

    return undefined;
}

function readErrorRetry(error: object): ErrorRetry | undefined {
    if (!(ERROR_RETRY in error)) {
        return undefined;
    }

    const retry = error[ERROR_RETRY];

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип значения по symbol-ключу не выводится, сузили проверкой typeof
    return typeof retry === 'function' ? (retry as ErrorRetry) : undefined;
}
